import React, { useState } from 'react';
import axios from '../../../utils/api/axios';
import toast from 'react-hot-toast';

interface AddEndpointModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    assetId: string;
    targetId?: string; // Make optional for backward compatibility
    availableTargets?: Array<{ target_id: string; target_name: string; target_value: string }>; // New prop
    programId: number;
}

const AddEndpointModal: React.FC<AddEndpointModalProps> = ({
    isOpen, onClose, onSuccess, assetId, targetId, availableTargets, programId
}) => {
    const [selectedTargetId, setSelectedTargetId] = useState(targetId || availableTargets?.[0]?.target_id || '');
    const [value, setValue] = useState('');
    const [resourceType, setResourceType] = useState('PATH');
    const [httpMethod, setHttpMethod] = useState('GET');
    const [notes, setNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Update selected target when targetId prop changes
    React.useEffect(() => {
        if (targetId) setSelectedTargetId(targetId);
        else if (availableTargets?.[0]) setSelectedTargetId(availableTargets[0].target_id);
    }, [targetId, availableTargets]);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!value.trim()) {
            toast.error("Endpoint value is required");
            return;
        }

        try {
            setIsSubmitting(true);
            await axios.post(`/assets/${assetId}/targets/${selectedTargetId}/resources`, {
                value: value.trim(),
                resource_type: resourceType,
                http_method: resourceType === 'PATH' ? httpMethod : null,
                notes: notes.trim(),
                program_id: programId,
                status: 'IN_SCOPE'
            });
            toast.success("Endpoint added to scope");
            onSuccess();
            onClose();
            setValue('');
            setNotes('');
        } catch (error: any) {
            console.error("Error adding endpoint:", error);
            toast.error(error.response?.data?.message || "Failed to add endpoint");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
                <div className="px-6 py-4 border-b flex justify-between items-center bg-[#010D2C] text-white">
                    <h3 className="text-lg font-medium">Add New Endpoint</h3>
                    <button onClick={onClose} className="text-white hover:text-gray-200">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {/* Target Selection - Only show if multiple targets available */}
                    {availableTargets && availableTargets.length > 1 && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Target</label>
                            <select
                                value={selectedTargetId}
                                onChange={(e) => setSelectedTargetId(e.target.value)}
                                className="w-full border rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-blue-500"
                                required
                            >
                                {availableTargets.map((target) => (
                                    <option key={target.target_id} value={target.target_id}>
                                        {target.target_name} ({target.target_value})
                                    </option>
                                ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">Select the target where this endpoint was discovered</p>
                        </div>
                    )}
                    {/* Show selected target info if only one available */}
                    {availableTargets && availableTargets.length === 1 && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-xs font-medium text-blue-800">Target</p>
                            <p className="text-sm text-blue-900 font-semibold">{availableTargets[0].target_name}</p>
                            <p className="text-xs text-blue-700">{availableTargets[0].target_value}</p>
                        </div>
                    )}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Resource Type</label>
                        <select
                            value={resourceType}
                            onChange={(e) => setResourceType(e.target.value)}
                            className="w-full border rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="PATH">Path (e.g. /api/v1/user)</option>
                            <option value="IP">IP Address</option>
                            <option value="PORT">Port</option>
                            <option value="FILE">File</option>
                            <option value="PARAMETER">Parameter</option>
                            <option value="HEADER">Header</option>
                            <option value="OTHER">Other</option>
                        </select>
                    </div>
                    {resourceType === 'PATH' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">HTTP Method</label>
                            <select
                                value={httpMethod}
                                onChange={(e) => setHttpMethod(e.target.value)}
                                className="w-full border rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="GET">GET</option>
                                <option value="POST">POST</option>
                                <option value="PUT">PUT</option>
                                <option value="DELETE">DELETE</option>
                                <option value="PATCH">PATCH</option>
                            </select>
                        </div>
                    )}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Endpoint Value</label>
                        <input
                            type="text"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            placeholder="e.g. /api/v1/login"
                            className="w-full border rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes / Discovery Context</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="How did you find this? Any specific context?"
                            className="w-full border rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
                        />
                    </div>
                    <div className="pt-4 flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border rounded-lg hover:bg-gray-50 transition"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-4 py-2 bg-[#010D2C] text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition"
                        >
                            {isSubmitting ? "Submitting..." : "Submit Discovery"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddEndpointModal;
